import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import {
  Home, History, Settings, User, Phone, Mail, Key, Zap, Flame, Trophy,
  Rocket, Gauge, Timer, Calendar, Check, ChevronRight, TrendingUp, Gift,
  Search, Target, Award, Crown, Shield,
} from "lucide-react";

/* Starda Casino — yellow / dark-navy racing theme
   Routes: /templates/starda/{main,history,settings,profile} (combined preview). */
export default function StardaCombinedPreview() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [browserTheme, setBrowserTheme] = useState("dark");
  const [currentPage, setCurrentPage] = useState("main");
  const [showBrowser, setShowBrowser] = useState(true);
  const [historyFilter, setHistoryFilter] = useState("all");
  const [listPage, setListPage] = useState(1);
  const [dateFromOverride, setDateFromOverride] = useState("");
  const [dateToOverride, setDateToOverride] = useState("");
  const ITEMS_PER_PAGE_LIST = 12;

  useEffect(() => {
    const dataParam = searchParams.get("data");
    const themeParam = searchParams.get("theme");
    const pageParam = searchParams.get("page");
    const browserParam = searchParams.get("browser");
    const filterParam = searchParams.get("filter");
    if (themeParam) setBrowserTheme(themeParam);
    if (browserParam === "false") setShowBrowser(false);
    if (filterParam && ["all", "deposit", "withdrawal"].includes(filterParam)) setHistoryFilter(filterParam);
    const listPageParam = parseInt(searchParams.get("listpage") || "1", 10);
    if (!isNaN(listPageParam) && listPageParam >= 1) setListPage(listPageParam);

    const path = location.pathname;
    if (pageParam && ["main", "history", "settings", "profile"].includes(pageParam)) {
      setCurrentPage(pageParam === "profile" ? "settings" : pageParam);
    } else if (path.includes("/history")) {
      setCurrentPage("history");
    } else if (path.includes("/settings") || path.includes("/profile")) {
      setCurrentPage("settings");
    } else {
      setCurrentPage("main");
    }

    if (dataParam) {
      try { setData(JSON.parse(decodeURIComponent(dataParam))); } catch (e) { console.error(e); }
    }
  }, [searchParams, location.pathname]);

  useEffect(() => {
    const titles = { main: "Гонка бонусов", history: "История операций", settings: "Профиль" };
    document.title = `${titles[currentPage] || "Starda"} | Starda Casino`;
    document.body.style.backgroundColor = "#0b1222";
  }, [currentPage]);

  const dateRange = useMemo(() => {
    if (!data || !data.transactions || !data.transactions.length) return { from: "", to: "" };
    const toSort = (d) => {
      const p = (d || "01.01.2000").split(".");
      return parseInt(p[2]) * 10000 + parseInt(p[1]) * 100 + parseInt(p[0]);
    };
    const sorted = [...data.transactions].filter((t) => t.date).sort((a, b) => toSort(a.date) - toSort(b.date));
    return { from: sorted[0]?.date || "", to: sorted[sorted.length - 1]?.date || "" };
  }, [data]);

  if (!data) return <div className="min-h-screen bg-[#0b1222] flex items-center justify-center text-yellow-300">Загрузка…</div>;

  const accountName = `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Пользователь";
  const userId = data.visibleId || data.userId || "0000000";
  const balance = parseFloat(data.balance || 0);
  const bonusBalance = parseFloat(data.bonusBalance || 0);
  const totalBalance = parseFloat(data.totalBalance || balance + bonusBalance);
  const fmt = (n) => parseFloat(n || 0).toLocaleString("ru-RU");

  // Casino invert: Ozon deposit -> casino "Вывод"; Ozon withdrawal -> casino "Пополнение"
  const casinoTxs = (data.transactions || []).map((t) => {
    const inv = t.type === "deposit" ? "withdrawal" : t.type === "withdrawal" ? "deposit" : t.type;
    return { ...t, type: inv };
  });
  const filteredTx = historyFilter === "all" ? casinoTxs : casinoTxs.filter((t) => t.type === historyFilter);

  const BrowserFrame = () => (
    <div style={{ backgroundColor: "#dee1e6" }} className="flex-shrink-0">
      <div className="flex items-center h-[38px] pl-2">
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] mr-[1px]" style={{ backgroundColor: "#c9cdd4" }}>
          <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2" />
          <span className="text-[13px] text-slate-500">Ozon Карта</span>
        </div>
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[200px]" style={{ backgroundColor: "#ffffff" }}>
          <div className="w-4 h-4 mr-2 rounded flex items-center justify-center" style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)" }}>
            <Rocket className="w-2.5 h-2.5 text-slate-900" />
          </div>
          <span className="text-[13px] text-slate-800">Starda Casino</span>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-4 gap-4 bg-white">
        <div className="flex gap-2">
          {["←", "→", "↻"].map((a, i) => (
            <button key={i} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 text-slate-500">{a}</button>
          ))}
        </div>
        <div className="flex-1 h-[32px] rounded-full px-4 flex items-center bg-slate-100">
          <span className="text-[13px] text-slate-600">🔒 starda-casino.com/cabinet/{userId}</span>
        </div>
      </div>
    </div>
  );

  // Racing-flag pattern for banner accent
  const stripeBg = "repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0 12px, transparent 12px 24px)";

  const MainPage = () => {
    const speed = Math.min(100, 38 + Math.floor(Math.random() * 55));
    return (
      <div className="p-6 space-y-5">
        {/* Hero banner — racing gauge */}
        <div className="rounded-2xl p-6 relative overflow-hidden" style={{ background: "linear-gradient(110deg, #0f172a 0%, #1e293b 50%, #fbbf24 200%)" }}>
          <div className="absolute inset-0 pointer-events-none opacity-40" style={{ background: stripeBg }} />
          <div className="absolute top-0 right-8 w-48 h-48 rounded-full blur-3xl opacity-25" style={{ background: "#fbbf24" }} />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] uppercase tracking-widest font-bold text-yellow-300">SEASON 5 · TURBO</span>
                <span className="w-1 h-1 rounded-full bg-yellow-300" />
                <span className="text-[11px] text-yellow-200/80">призы 50 000 000 ₽</span>
              </div>
              <div className="text-white/60 text-sm">Баланс пилота</div>
              <div className="flex items-end gap-3">
                <div className="text-4xl font-bold text-white font-mono">{fmt(balance)}</div>
                <div className="text-yellow-300 text-xl font-mono">₽</div>
              </div>
              <div className="flex gap-4 mt-3 text-sm">
                <div className="text-yellow-300 font-medium flex items-center gap-1"><Gift className="w-4 h-4" /> Бонус: {fmt(bonusBalance)} ₽</div>
                <div className="text-white/70 flex items-center gap-1"><Zap className="w-4 h-4 text-yellow-300" /> Фриспины: {data.freeSpins || 0}</div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-3">
              <button className="px-5 py-2.5 rounded-lg font-bold text-slate-900 text-sm" style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)" }}>
                ⚡ Турбо-пополнение
              </button>
              <div className="flex items-center gap-2 text-[12px] text-white/60">
                <Gauge className="w-4 h-4 text-yellow-300" />
                Скорость лиги: <span className="text-yellow-300 font-bold">{speed} / 100</span>
              </div>
            </div>
          </div>

          {/* Speedometer progress */}
          <div className="relative mt-4">
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${speed}%`, background: "linear-gradient(90deg, #ef4444, #fbbf24, #22c55e)" }} />
            </div>
            <div className="flex justify-between text-[10px] text-white/40 mt-1">
              <span>0</span>
              <span className="text-yellow-300">Pit stop · {data.vipLevel || "Racer"}</span>
              <span>100</span>
            </div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Кругов сыграно", value: data.totalGames || 0, icon: Flame, accent: "#fbbf24" },
            { label: "Побед в лиге", value: data.totalWins || 0, icon: Trophy, accent: "#22c55e" },
            { label: "Общий оборот", value: fmt(data.totalTurnover || 0) + " ₽", icon: TrendingUp, accent: "#60a5fa" },
            { label: "Лучшая серия", value: "x" + (data.bestStreak || Math.floor(3 + Math.random() * 12)), icon: Target, accent: "#a855f7" },
          ].map((s, i) => (
            <div key={i} className="rounded-xl p-4 border" style={{ background: "#0f172a", borderColor: "rgba(251, 191, 36, 0.15)" }}>
              <div className="flex items-center gap-2 mb-2">
                <s.icon className="w-4 h-4" style={{ color: s.accent }} />
                <span className="text-[11px] text-white/50 uppercase tracking-wide">{s.label}</span>
              </div>
              <div className="text-white text-xl font-bold font-mono">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Top games — racer theme */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-yellow-300 text-lg flex items-center gap-2"><Rocket className="w-5 h-5" /> Гоночная трасса</h3>
            <button className="text-sm text-yellow-300 hover:text-yellow-200">Все игры →</button>
          </div>
          <div className="grid grid-cols-6 gap-3">
            {[
              { name: "Aviator Turbo", tag: "CRASH", grad: "linear-gradient(135deg,#be123c,#f97316)" },
              { name: "Book of Ra", tag: "SLOT", grad: "linear-gradient(135deg,#ca8a04,#78350f)" },
              { name: "Speed Baccarat", tag: "LIVE", grad: "linear-gradient(135deg,#166534,#064e3b)" },
              { name: "Sweet Bonanza", tag: "SLOT", grad: "linear-gradient(135deg,#db2777,#c026d3)" },
              { name: "Roulette Turbo", tag: "LIVE", grad: "linear-gradient(135deg,#1d4ed8,#1e3a8a)" },
              { name: "Gates Olympus", tag: "SLOT", grad: "linear-gradient(135deg,#7e22ce,#4c1d95)" },
            ].map((g, i) => (
              <div key={i} className="rounded-xl p-3 cursor-pointer hover:-translate-y-0.5 transition-transform border border-white/5" style={{ background: g.grad }}>
                <div className="h-20 rounded-lg bg-black/30 flex items-center justify-center mb-2 relative overflow-hidden">
                  <Rocket className="w-8 h-8 text-white/70" />
                  <span className="absolute top-1 right-1 text-[9px] font-bold px-1.5 rounded bg-yellow-300 text-slate-900">{g.tag}</span>
                </div>
                <div className="text-white text-xs font-medium truncate">{g.name}</div>
                <div className="text-white/60 text-[10px] flex items-center gap-1">
                  <Flame className="w-2.5 h-2.5 text-orange-300" />x{(Math.random() * 6 + 2).toFixed(1)}k hot
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Last transactions preview */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-yellow-300 text-lg">Последние заезды</h3>
            <button onClick={() => setCurrentPage("history")} className="text-sm text-yellow-300 hover:text-yellow-200">Все →</button>
          </div>
          <div className="rounded-xl border divide-y" style={{ background: "#0f172a", borderColor: "rgba(251, 191, 36, 0.15)", borderColor: "rgba(255,255,255,0.06)" }}>
            {casinoTxs.slice(0, 5).map((t, i) => {
              const isDep = t.type === "deposit";
              return (
                <div key={i} className="p-3 flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: isDep ? "rgba(251, 191, 36, 0.15)" : "rgba(239, 68, 68, 0.15)" }}>
                      {isDep ? <Rocket className="w-4 h-4 text-yellow-300" /> : <Timer className="w-4 h-4 text-red-400" />}
                    </div>
                    <div>
                      <div className="text-white text-sm">{isDep ? "Пополнение счёта" : "Вывод выигрыша"}</div>
                      <div className="text-[11px] text-white/40">{t.date} {t.time || ""}</div>
                    </div>
                  </div>
                  <div className={`font-mono font-semibold text-sm ${isDep ? "text-yellow-300" : "text-red-400"}`}>
                    {isDep ? "+" : "−"}{fmt(t.amount)} ₽
                  </div>
                </div>
              );
            })}
            {casinoTxs.length === 0 && <div className="p-6 text-center text-white/40 text-sm">Нет операций</div>}
          </div>
        </div>
      </div>
    );
  };

  const HistoryPage = () => (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-yellow-300 flex items-center gap-2"><History className="w-6 h-6" /> Журнал операций</h2>
        <div className="flex items-center gap-2 bg-[#0f172a] rounded-lg px-3 py-2 text-sm" style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
          <Search className="w-4 h-4 text-white/40" />
          <span className="text-white/40">Поиск по ID…</span>
        </div>
      </div>

      <div className="rounded-xl p-4 mb-2" style={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex items-center gap-3 flex-wrap">
          {[
            { id: "all", label: "Все" },
            { id: "deposit", label: "Пополнения" },
            { id: "withdrawal", label: "Выводы" },
          ].map((f) => (
            <button key={f.id} onClick={() => { setHistoryFilter(f.id); setListPage(1); }} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${historyFilter === f.id ? "text-slate-900" : "text-white/60 hover:text-white"}`} style={{ background: historyFilter === f.id ? "linear-gradient(135deg, #fbbf24, #f59e0b)" : "#0b1222", border: "1px solid rgba(255,255,255,0.06)" }}>{f.label}</button>
          ))}
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white/70">С:</span>
            <div className="rounded-lg px-3 py-1.5 text-white text-sm min-w-[130px] flex items-center justify-between gap-2" style={{ background: "#0b1222", border: "1px solid rgba(255,255,255,0.08)" }}>
              <span>{dateRange.from || '—'}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/40"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white/70">По:</span>
            <div className="rounded-lg px-3 py-1.5 text-white text-sm min-w-[130px] flex items-center justify-between gap-2" style={{ background: "#0b1222", border: "1px solid rgba(255,255,255,0.08)" }}>
              <span>{dateRange.to || '—'}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/40"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
            </div>
          </div>
          <button className="px-4 py-1.5 rounded-lg text-slate-900 text-sm font-bold" style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)" }}>Применить</button>
        </div>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="grid grid-cols-[1fr_140px_140px_140px] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-yellow-300/70 font-bold" style={{ background: "rgba(0,0,0,0.25)" }}>
          <div>Операция</div>
          <div>Канал</div>
          <div className="text-right">Сумма</div>
          <div className="text-right">Дата</div>
        </div>
        <div className="divide-y" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
          {filteredTx.length === 0 && <div className="p-8 text-center text-white/40 text-sm">Нет операций по выбранным фильтрам</div>}
          {filteredTx.slice((listPage - 1) * ITEMS_PER_PAGE_LIST, listPage * ITEMS_PER_PAGE_LIST).map((t, i) => {
            const isDep = t.type === "deposit";
            return (
              <div key={i} className="grid grid-cols-[1fr_140px_140px_140px] gap-3 px-5 py-3 items-center hover:bg-white/5 transition-colors" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: isDep ? "rgba(251, 191, 36, 0.15)" : "rgba(239, 68, 68, 0.15)" }}>
                    {isDep ? <Rocket className="w-4 h-4 text-yellow-300" /> : <Timer className="w-4 h-4 text-red-400" />}
                  </div>
                  <div>
                    <div className="text-white text-sm">{isDep ? "Турбо-пополнение" : "Вывод на банк"}</div>
                    <div className="text-[11px] text-white/40 flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-400" />
                      Подтверждена · ID #{(10000 + i * 37 + (t.date || "").length).toString().slice(-6)}
                    </div>
                  </div>
                </div>
                <div className="text-white/70 text-sm">Ozon {(((t.category || "").trim() === "Финансовые услуги") ? "Карта" : (((t.category || "").trim() === "Пополнения") ? "СБП" : ((t.method === "Карта" || t.method === "СБП") ? t.method : "СБП")))}</div>
                <div className={`text-right font-mono font-semibold ${isDep ? "text-yellow-300" : "text-red-400"}`}>{isDep ? "+" : "−"}{fmt(t.amount)} ₽</div>
                <div className="text-right text-white/70 text-sm">{t.date}<div className="text-[11px] text-white/40">{t.time || ""}</div></div>
              </div>
            );
          })}
        </div>
        {filteredTx.length > 0 && (() => {
          const totalPages = Math.max(1, Math.ceil(filteredTx.length / ITEMS_PER_PAGE_LIST));
          const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
          return (
            <div className="px-5 py-3 flex items-center justify-between text-sm text-white/50 border-t border-white/5">
              <span>Показано {(listPage - 1) * ITEMS_PER_PAGE_LIST + 1}–{Math.min(listPage * ITEMS_PER_PAGE_LIST, filteredTx.length)} из {filteredTx.length} операций</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setListPage(Math.max(1, listPage - 1))} disabled={listPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: "#0b1222", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                {pages.map(p => (
                  <button key={p} onClick={() => setListPage(p)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors"
                    style={p === listPage
                      ? { background: "linear-gradient(135deg, #fbbf24, #f59e0b)", color: "#0f172a" }
                      : { background: "#0b1222", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" }}>
                    {p}
                  </button>
                ))}
                <button onClick={() => setListPage(Math.min(totalPages, listPage + 1))} disabled={listPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: "#0b1222", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );

  const SettingsPage = () => {
    const totalGames = data.totalGames || Math.floor(120 + Math.random() * 300);
    const totalWins = data.totalWins || Math.floor(60 + Math.random() * 150);
    const winRate = totalGames ? Math.round((totalWins / totalGames) * 100) : 0;
    const xpProgress = 73;

    return (
      <div className="p-6 space-y-5 max-w-5xl">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-2xl font-bold text-yellow-300 flex items-center gap-2">
              <User className="w-6 h-6" /> Профиль пилота
            </h2>
            <p className="text-sm text-white/50 mt-1">Персональные данные и игровая статистика</p>
          </div>
          <button className="px-4 h-10 rounded-lg text-slate-900 text-sm font-bold hover:brightness-110 transition-all" style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)" }}>
            Редактировать
          </button>
        </div>

        <div className="grid grid-cols-[340px_1fr] gap-5">
          <div className="rounded-2xl p-6 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #0f172a 0%, #0b1222 100%)", border: "1px solid rgba(251, 191, 36, 0.2)" }}>
            <div className="absolute top-0 right-0 w-40 h-40 blur-3xl opacity-20" style={{ background: "#fbbf24", transform: "translate(40%, -40%)" }} />
            <div className="relative flex flex-col items-center text-center">
              <div className="w-28 h-28 rounded-full flex items-center justify-center text-slate-900 font-bold text-5xl shadow-lg shadow-yellow-500/20" style={{ background: "linear-gradient(135deg, #fde68a, #f59e0b)" }}>
                {(accountName[0] || "U").toUpperCase()}
              </div>
              <h3 className="text-white text-xl font-bold mt-4">{accountName}</h3>
              <div className="text-white/50 text-sm mt-1">ID: {userId}</div>

              <div className="mt-4 px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5" style={{ background: "rgba(34, 197, 94, 0.15)", color: "#4ade80", border: "1px solid rgba(74, 222, 128, 0.3)" }}>
                <Shield className="w-3.5 h-3.5" /> Верифицирован · KYC подтверждён
              </div>

              <div className="w-full mt-6 pt-6 border-t border-white/5 grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-yellow-300 font-bold text-xl">{totalGames}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-wider mt-1">Заездов</div>
                </div>
                <div>
                  <div className="text-green-400 font-bold text-xl">{totalWins}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-wider mt-1">Побед</div>
                </div>
                <div>
                  <div className="text-pink-400 font-bold text-xl">#{data.rank || 42}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-wider mt-1">В рейтинге</div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-6" style={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white text-lg font-semibold">Личные данные</h3>
              <span className="text-[11px] text-yellow-300/70 uppercase tracking-widest font-bold">Подтверждены</span>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              {[
                { icon: User, label: "ФИО", value: accountName, verified: true },
                { icon: Mail, label: "E-mail", value: data.email || "—", verified: !!data.email },
                { icon: Phone, label: "Телефон", value: data.phone || "—", verified: !!data.phone },
                { icon: Calendar, label: "Дата регистрации", value: data.registrationDate || "03.09.2024", verified: true },
                { icon: Key, label: "Пароль", value: "Смена " + (data.passwordChangedDays || 14) + " дн. назад", verified: true },
                { icon: Shield, label: "2FA-защита", value: "Включена", verified: true },
              ].map((it, i) => (
                <div key={i} className="border-b border-white/5 pb-4 last:border-0">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(251, 191, 36, 0.12)" }}>
                      <it.icon className="w-3.5 h-3.5 text-yellow-300" />
                    </div>
                    <span className="text-[11px] text-white/40 uppercase tracking-wider font-semibold">{it.label}</span>
                    {it.verified && (
                      <div className="ml-auto flex items-center gap-1 text-[10px] text-green-400">
                        <Check className="w-3 h-3" strokeWidth={3} /> Подтверждено
                      </div>
                    )}
                  </div>
                  <div className="text-white text-[15px] font-medium pl-9">{it.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl p-6 relative overflow-hidden" style={{ background: "linear-gradient(110deg, #0f172a 0%, #1c1917 50%, #422006 100%)", border: "1px solid rgba(251, 191, 36, 0.3)" }}>
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-25" style={{ background: "#fbbf24", transform: "translate(30%, -30%)" }} />
          <div className="relative grid grid-cols-[auto_1fr_auto] gap-6 items-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(251,191,36,0.2), rgba(245,158,11,0.1))", border: "1px solid rgba(251, 191, 36, 0.3)" }}>
              <Trophy className="w-8 h-8 text-yellow-300" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[11px] uppercase tracking-[0.2em] text-yellow-300/80 font-semibold">Текущая лига</span>
                <span className="w-1 h-1 rounded-full bg-yellow-300/40" />
                <span className="text-[11px] text-white/40">Кэшбэк {data.cashbackPercent || 12}%</span>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="text-white text-2xl font-bold">{data.vipLevel || "Bronze Racer"}</span>
                <span className="text-white/40 text-sm">· Персональный менеджер</span>
              </div>
              <div className="mt-3 max-w-md">
                <div className="flex justify-between text-[12px] text-white/50 mb-1">
                  <span>До следующего ранга</span>
                  <span className="text-yellow-300 font-semibold">{xpProgress}/100 XP</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${xpProgress}%`, background: "linear-gradient(90deg, #fbbf24, #f59e0b)" }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-5 pl-6 border-l border-white/10">
              <div className="text-center">
                <div className="text-yellow-300 font-bold text-xl">{data.tournaments || 18}</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider mt-1">Турниров</div>
              </div>
              <div className="text-center">
                <div className="text-yellow-300 font-bold text-xl">{winRate}%</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider mt-1">Win rate</div>
              </div>
              <div className="text-center">
                <div className="text-yellow-300 font-bold text-xl">×{data.bestStreak || 9}</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider mt-1">Макс. серия</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen template-preview-scale" style={{ background: "#0b1222" }}>
      {showBrowser && <BrowserFrame />}
      <div className="flex" style={{ minHeight: showBrowser ? "calc(100vh - 78px)" : "100vh" }}>
        <div className="w-64 flex flex-col" style={{ background: "linear-gradient(180deg, #0f172a 0%, #0b1222 100%)", borderRight: "1px solid rgba(251, 191, 36, 0.15)" }}>
          <div className="p-4 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)" }}>
                <Rocket className="w-5 h-5 text-slate-900" />
              </div>
              <div>
                <div className="font-bold text-yellow-300">STARDA</div>
                <div className="text-[11px] text-white/50 tracking-wider">CASINO · RACING LEAGUE</div>
              </div>
            </div>
          </div>
          <div className="flex-1 py-2">
            {[
              { id: "main", icon: Home, label: "Гараж" },
              { id: "history", icon: History, label: "Журнал" },
              { id: "settings", icon: Settings, label: "Профиль" },
            ].map((it) => (
              <button key={it.id} onClick={() => setCurrentPage(it.id)} className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${currentPage === it.id ? "text-yellow-300" : "text-white/60 hover:bg-white/5 hover:text-white"}`} style={{ background: currentPage === it.id ? "rgba(251, 191, 36, 0.1)" : "transparent", borderRight: currentPage === it.id ? "2px solid #fbbf24" : "none" }}>
                <it.icon className="w-5 h-5" /><span className="text-sm">{it.label}</span>
              </button>
            ))}
          </div>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-slate-900 font-bold" style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)" }}>{(accountName[0] || "U").toUpperCase()}</div>
              <div className="min-w-0">
                <div className="font-medium truncate text-white text-sm">{accountName}</div>
                <div className="text-[11px] text-white/40">ID: {userId}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" style={{ background: "#0b1222" }}>
          {currentPage === "main" && <MainPage />}
          {currentPage === "history" && <HistoryPage />}
          {currentPage === "settings" && <SettingsPage />}
        </div>
      </div>
    </div>
  );
}
